import { useNavigate, useOutletContext } from 'react-router-dom';
import type { SignedInShellOutletContext } from '../components/shell/SignedInShell';
import {
  Banner,
  BannerInvite,
  Button,
  Card,
  EmptyState,
  KindGlyph,
  MonoLabel,
  Pill,
  Row,
  Skeleton,
  StepMarker,
  Tag,
  TypeChip,
  Avatar
} from '../components/primitives';
import { useDashboardVM } from '../lib/vm/useDashboardVM';
import {
  useOnboardingChecklistVM,
  type UseOnboardingChecklistVM
} from '../lib/vm/useOnboardingChecklistVM';
import {
  isOverdue,
  pillStatus,
  sortPendingWork,
  sortRecentDocs,
  CARD_ROW_CAP,
  type ActivityEvent,
  type PendingInvitationItem,
  type PendingWorkItem,
  type RecentDocItem
} from '../lib/models/dashboard';
import { formatRelativeTime } from '../lib/relativeTime';
import './Dashboard.css';

/**
 * `/dashboard` surface — populated-mode card stack + onboarding checklist.
 *
 * Dashboard is a read-only aggregator. Turn 12.1 wired the route + VM + page
 * chrome; 12.2 filled `.db-body` with the populated card stack — four cards
 * (Pending work / Your todos / Recent documents / Activity) plus the
 * pending-invitations strip. Turn 12.3 adds the fresh-org branch: when
 * `useDashboardVM` reports `mode === 'onboarding'`, the populated stack is
 * swapped for the Welcome 5-step checklist (`OnboardingChecklist`, backed by
 * `useOnboardingChecklistVM`). "Hide checklist" falls back to the populated
 * stack and surfaces a header "Onboarding" link to re-show it.
 *
 * The backing tables + `get_dashboard` RPC land in the Phase 3 schema turns,
 * so until then every VM array is empty and each card shows its empty state.
 */
const ROW_TEMPLATE = '28px minmax(0, 1fr) auto';
const ACT_TEMPLATE = '22px minmax(0, 1fr) auto';

function PendingWorkRow({ item }: { item: PendingWorkItem }) {
  const overdue = isOverdue(item);
  return (
    <Row
      template={ROW_TEMPLATE}
      overdue={overdue}
      className="db-card-row"
      data-testid="dashboard-pw-row"
    >
      <KindGlyph>{item.kind === 'task' ? 'TSK' : 'DOC'}</KindGlyph>
      <div className="db-row-main">
        <div className="db-row-title">{item.title}</div>
        <div className="db-row-meta">
          {item.kind === 'doc' && item.docType ? (
            <TypeChip>{item.docType}</TypeChip>
          ) : null}
          {item.status ? (
            <Pill status={pillStatus(item.status)}>{item.status}</Pill>
          ) : null}
        </div>
      </div>
      <div className="db-row-right">
        <span>{formatRelativeTime(item.lastEventAt)}</span>
        {item.dueAt ? (
          <span className={overdue ? 'db-row-due-overdue' : 'db-row-due'}>
            Due {formatRelativeTime(item.dueAt)}
          </span>
        ) : null}
      </div>
    </Row>
  );
}

function RecentDocRow({ item }: { item: RecentDocItem }) {
  return (
    <Row
      template={ROW_TEMPLATE}
      className="db-card-row"
      data-testid="dashboard-doc-row"
    >
      <KindGlyph>DOC</KindGlyph>
      <div className="db-row-main">
        <div className="db-row-title">{item.title}</div>
        <div className="db-row-meta">
          {item.docType ? <TypeChip>{item.docType}</TypeChip> : null}
          {item.status ? (
            <Pill status={pillStatus(item.status)}>{item.status}</Pill>
          ) : null}
        </div>
      </div>
      <div className="db-row-right">
        {item.actorName ? (
          <span className="db-row-who">
            <Avatar name={item.actorName} size="sm" />
            {item.actorName}
          </span>
        ) : null}
        <span>{formatRelativeTime(item.lastEventAt)}</span>
      </div>
    </Row>
  );
}

function ActivityRow({ event }: { event: ActivityEvent }) {
  return (
    <Row
      template={ACT_TEMPLATE}
      className="db-card-row"
      data-testid="dashboard-activity-row"
    >
      <Avatar name={event.actorName} size="sm" />
      <div className="db-act-text">
        <span className="db-act-actor">{event.actorName}</span>
        {event.actorArchived ? <Tag>Archived</Tag> : null}{' '}
        <span className="db-act-verb">{event.verb}</span>{' '}
        <span className="db-act-subject">
          {event.subjectType}
          {event.subjectTitle ? ` "${event.subjectTitle}"` : ''}
        </span>
      </div>
      <span className="db-act-when">
        {formatRelativeTime(event.occurredAt)}
      </span>
    </Row>
  );
}

function InvitationRow({
  item,
  onAccept,
  onDecline
}: {
  item: PendingInvitationItem;
  onAccept?: (token: string) => void;
  onDecline?: (invitationId: string) => void;
}) {
  const meta = [
    item.roleLabel,
    item.inviterName ? `invited by ${item.inviterName}` : null,
    item.invitedAt ? formatRelativeTime(item.invitedAt) : null
  ]
    .filter(Boolean)
    .join(' · ');
  return (
    <BannerInvite
      data-testid="dashboard-invitation"
      mark={(item.orgName[0] ?? '?').toUpperCase()}
      title={
        <>
          Pending invitation — <strong>{item.orgName}</strong>
        </>
      }
      meta={meta}
      actions={
        <>
          <Button
            variant="default"
            size="sm"
            data-testid="dashboard-invitation-decline"
            onClick={() => onDecline?.(item.id)}
          >
            Decline
          </Button>
          <Button
            variant="primary"
            size="sm"
            data-testid="dashboard-invitation-accept"
            onClick={() => onAccept?.(item.token)}
            disabled={!item.token}
          >
            Accept &amp; switch
          </Button>
        </>
      }
    />
  );
}

/**
 * Fresh-org onboarding checklist — replaces the populated card stack when
 * `useDashboardVM` reports `mode === 'onboarding'` and the user hasn't hidden
 * it. Composes `StepMarker` + `Button` + `Tag` primitives; `.db-onboard`
 * layout is page CSS. The "go" action marks the step done, then routes to the
 * step's destination (step 1 has none — it just confirms).
 */
function OnboardingChecklist({ ob }: { ob: UseOnboardingChecklistVM }) {
  const navigate = useNavigate();
  return (
    <Card className="db-onboard" data-testid="dashboard-onboarding">
      <h2 className="db-onboard-h2">Welcome to Penrose</h2>
      <p className="db-onboard-lede">
        Set up your org so every member&rsquo;s agent reads the same context.
        Five steps.
      </p>
      {ob.steps.map((step) => {
        const state = ob.stepStates[step.n];
        const done = state === 'done';
        return (
          <div
            key={step.n}
            className="db-onboard-row"
            data-state={state}
            data-testid="dashboard-onboard-step"
          >
            <StepMarker state={state} />
            <div className="db-onboard-text">
              <div className="db-onboard-title">
                Step {step.n}: {step.title}
              </div>
              <div className="db-onboard-desc">{step.desc}</div>
            </div>
            <div className="db-onboard-actions">
              {done ? (
                <Tag data-testid="dashboard-onboard-done">Done</Tag>
              ) : (
                <>
                  <Button
                    variant="primary"
                    size="sm"
                    data-testid="dashboard-onboard-go"
                    onClick={() => {
                      ob.markDone(step.n);
                      if (step.destination) navigate(step.destination);
                    }}
                  >
                    {step.goLabel}
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="db-onboard-skip"
                    data-testid="dashboard-onboard-skip"
                    onClick={() => ob.skip(step.n)}
                  >
                    Skip
                  </Button>
                </>
              )}
            </div>
          </div>
        );
      })}
      <div className="db-onboard-footer">
        <MonoLabel
          size="sm"
          tone="muted"
          className="db-onboard-progress"
          data-testid="dashboard-onboard-progress"
        >
          {ob.progressLabel}
        </MonoLabel>
        <Button
          variant="ghost"
          size="sm"
          className="db-onboard-hide"
          data-testid="dashboard-onboard-hide"
          onClick={ob.hide}
        >
          Hide checklist
        </Button>
      </div>
    </Card>
  );
}

export function Dashboard() {
  const vm = useDashboardVM();
  const onboarding = useOnboardingChecklistVM();
  const navigate = useNavigate();
  // 17.3b.2 — Optional outlet context: outside `SignedInShell` (e.g. the
  // primitives playground page) the dashboard renders with no context;
  // `useOutletContext` returns undefined there and the buttons no-op.
  const shellContext = useOutletContext<
    SignedInShellOutletContext | undefined
  >();

  const loading = vm.status === 'loading';
  const errored = vm.status === 'error';

  const onboardingMode = vm.mode === 'onboarding';
  const showChecklist = onboardingMode && !onboarding.hidden;

  const lastRefreshed = (() => {
    if (loading || vm.refreshing) return 'Last refreshed: refreshing…';
    if (vm.lastRefreshedAt == null) return 'Last refreshed: —';
    if (Date.now() - vm.lastRefreshedAt < 45_000) {
      return 'Last refreshed: just now';
    }
    return `Last refreshed: ${formatRelativeTime(
      new Date(vm.lastRefreshedAt).toISOString()
    )}`;
  })();

  const pendingWork = sortPendingWork(vm.pendingWork).slice(0, CARD_ROW_CAP);
  const recentDocs = sortRecentDocs(vm.recentDocs).slice(0, CARD_ROW_CAP);

  return (
    <section
      className="db-page"
      data-testid="dashboard-page"
      data-mode={vm.mode}
      data-status={vm.status}
      data-pending-work-count={vm.pendingWork.length}
      data-recent-docs-count={vm.recentDocs.length}
      data-activity-count={vm.activity.length}
      data-invitation-count={vm.pendingInvitations.length}
      data-onboarding-visible={String(showChecklist)}
    >
      <header className="db-header">
        <h1 className="db-h1">Dashboard</h1>
        {onboardingMode && onboarding.hidden ? (
          <Button
            variant="ghost"
            size="sm"
            className="db-onboard-link"
            data-testid="dashboard-onboard-link"
            onClick={onboarding.show}
          >
            Onboarding
          </Button>
        ) : null}
        <MonoLabel
          size="sm"
          tracking="wide"
          tone="muted"
          className="db-last-refreshed"
          data-testid="dashboard-last-refreshed"
        >
          {lastRefreshed}
        </MonoLabel>
        <Button
          variant="default"
          size="sm"
          onClick={vm.refresh}
          disabled={loading || vm.refreshing || vm.status === 'unauth'}
          data-testid="dashboard-refresh-btn"
        >
          Refresh
        </Button>
      </header>

      {loading ? (
        <div className="db-skeleton" data-testid="dashboard-skeleton">
          <Skeleton width="100%" height={120} />
          <Skeleton width="100%" height={120} />
          <Skeleton width="100%" height={120} />
        </div>
      ) : errored ? (
        <Banner kind="error" title="Couldn't load your dashboard">
          {vm.error ?? 'Refresh the page or sign out and back in.'}
        </Banner>
      ) : showChecklist ? (
        <OnboardingChecklist ob={onboarding} />
      ) : (
        <div className="db-body" data-testid="dashboard-body">
          {vm.pendingInvitations.length > 0 ? (
            <div className="db-invitations" data-testid="dashboard-invitations">
              {vm.pendingInvitations.map((inv) => (
                <InvitationRow
                  key={inv.id}
                  item={inv}
                  onAccept={shellContext?.acceptInvitation}
                  onDecline={shellContext?.declineInvitation}
                />
              ))}
            </div>
          ) : null}

          <Card data-testid="dashboard-card-pending-work">
            <Card.Head
              title="Pending work"
              count={pendingWork.length}
              grow
              viewAll="View all"
              onViewAll={() => navigate('/work-items')}
            />
            {pendingWork.length === 0 ? (
              <EmptyState data-testid="dashboard-pw-empty">
                Nothing on your plate right now.
              </EmptyState>
            ) : (
              pendingWork.map((item) => (
                <PendingWorkRow key={item.id} item={item} />
              ))
            )}
          </Card>

          <Card data-testid="dashboard-card-todos">
            <Card.Head
              title="Your todos"
              count={0}
              grow
              viewAll="All todos"
              onViewAll={() => navigate('/work-items')}
            />
            <EmptyState data-testid="dashboard-todos-empty">
              No personal todos yet.
            </EmptyState>
          </Card>

          <Card data-testid="dashboard-card-recent-docs">
            <Card.Head
              title="Recent documents"
              count={recentDocs.length}
              grow
              viewAll="View all"
              onViewAll={() => navigate('/documents')}
            />
            {recentDocs.length === 0 ? (
              <EmptyState data-testid="dashboard-docs-empty">
                No recent documents.
              </EmptyState>
            ) : (
              recentDocs.map((item) => (
                <RecentDocRow key={item.id} item={item} />
              ))
            )}
          </Card>

          <Card data-testid="dashboard-card-activity">
            <Card.Head
              title="Activity"
              count={vm.activity.length}
              grow
              viewAll="View all"
              onViewAll={() => navigate('/documents')}
            />
            {vm.activity.length === 0 ? (
              <EmptyState data-testid="dashboard-activity-empty">
                No activity yet.
              </EmptyState>
            ) : (
              vm.activity.map((event) => (
                <ActivityRow key={event.id} event={event} />
              ))
            )}
          </Card>
        </div>
      )}
    </section>
  );
}
